/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

const liveSearchSort = {
    sortOptions: [
        {
            label: window.liveSearchConfig.sortOptionsLabels["position"],
            name: "position",
            params: {
                // Use position for catalog view and relevance for search view.
                // relevance must be used for search view to reflect same order as in Backend.
                // Same reason for direction parameter
                attribute: window.liveSearchConfig.view === "catalog" ? "position" : "relevance",
                direction: window.liveSearchConfig.view === "catalog" ? "ASC" : "DESC"
            }
        },
        {
            label: window.liveSearchConfig.sortOptionsLabels["price_asc"],
            name: "price_asc",
            params: {
                attribute: "price",
                direction: "ASC"
            }
        },
        {
            label: window.liveSearchConfig.sortOptionsLabels["price_desc"],
            name: "price_desc",
            params: {
                attribute: "price",
                direction: "DESC"
            }
        }
    ],
    selectedSortOptionName: "",
    activeOption: null,
    sortExpanded: false,
    /**
     * Execute after init of the component
     */
    initSort() {
        this.selectedSortOptionName = this.getSortOptionName();
        this.activeOption = this.selectedSortOptionName;

        this.$watch("selectedSortOptionName", () => {
            this.updateSortOptionParams();
            this.pagesLoaded = [];
            this.getProducts("sort", 1);
        });

        this.setSortDefaults();
    },
    /**
     * Get the sort option name from local storage or set the default sort option
     *
     * @returns {string}
     */
    getSortOptionName() {
        const [navigationEntry] = performance.getEntriesByType("navigation");
        const isBackForward = navigationEntry && navigationEntry.type === "back_forward";

        // If we are navigating back or forward, use the last selected sort option parameters
        // Otherwise, use the first sort option parameters
        this.selectedSortOptionParams = isBackForward
            ? this.selectedSortOptionParams
            : this.sortOptions[0].params;

        // Find the sort option that matches the selected parameters
        const selectedOption = this.sortOptions.find(option =>
            JSON.stringify(option.params) === JSON.stringify(this.selectedSortOptionParams)
        );
        // Return the name of the selected option or the first option if not found
        return selectedOption ? selectedOption.name : this.sortOptions[0].name;
    },
    /**
     * Update the selected sort option parameters in the store
     */
    updateSortOptionParams() {
        // Find the selected sort option based on the selected sort option name
        const selectedOption = this.sortOptions.find(option => option.name === this.selectedSortOptionName);

        // Update the selected sort option parameters in the store
        this.selectedSortOptionParams = selectedOption ? selectedOption.params : {};
    },
    /**
     * Get the label of the selected sort option
     *
     * @returns {string}
     */
    getSelectedSortOptionLabel() {
        // Find the selected sort option based on the selected sort option name and return its label
        const selectedOption = this.sortOptions.find(option => option.name === this.selectedSortOptionName);
        return selectedOption.label;
    },
    /**
     * Set sort defaults if none are selected
     * Further sort option logic in catalogSort() alpine component
     */
    setSortDefaults() {
        if (Object.keys(this.selectedSortOptionParams).length === 0) {
            this.selectedSortOptionParams = this.sortOptions[0].params;
        }
    },
    /**
     * Get sort options for live search query
     *
     * @returns {({attribute: string, direction: string}|{}|*)[]}
     */
    getSortOptions() {
        const defaultSort = [
            {
                attribute: "relevance",
                direction: "DESC",
            },
        ];
        const isSelectedSortOptionParamsEmpty = Object.keys(this.selectedSortOptionParams).length === 0;
        const sortOptions = !isSelectedSortOptionParamsEmpty ? [this.selectedSortOptionParams] : defaultSort;

        if (this.view !== "catalog") {
            // attribute position can not be used in search view
            if (sortOptions[0].attribute === "position") {
                sortOptions[0].attribute = "relevance";
            }
        }
        return sortOptions;
    },
    activeOptionId() {
        return this.activeOption !== null ? 'option-' + this.sortOptions.findIndex(o => o.name === this.activeOption) : null;
    },
    toggleDropdown() {
        this.sortExpanded = !this.sortExpanded;
        if (this.sortExpanded) {
            this.activeOption = this.selectedSortOptionName;
            this.$nextTick(() => this.$refs.listbox.focus());
        }
    },
    openAndFocusFirstOption() {
        this.sortExpanded = true;
        this.activeOption = this.sortOptions[0].name;
        this.$nextTick(() => this.$refs.listbox.focus());
    },
    openAndFocusLastOption() {
        this.sortExpanded = true;
        this.activeOption = this.sortOptions[this.sortOptions.length - 1].name;
        this.$nextTick(() => this.$refs.listbox.focus());
    },
    /**
     * Select a sort option and close the dropdown
     *
     * @param name
     */
    selectOption(name) {
        this.selectedSortOptionName = name;
        this.activeOption = name;
        this.sortExpanded = false;
        this.$nextTick(() => this.$refs.button?.focus());
    },
    /**
     * Handle keyboard navigation for the sort options listbox
     *
     * @param event
     */
    handleListBoxKeyboard(event) {
        const index = this.sortOptions.findIndex(opt => opt.name === this.activeOption);
        if (event.key === 'ArrowDown') {
            const next = (index + 1) % this.sortOptions.length;
            this.activeOption = this.sortOptions[next].name;
        } else if (event.key === 'ArrowUp') {
            const prev = (index - 1 + this.sortOptions.length) % this.sortOptions.length;
            this.activeOption = this.sortOptions[prev].name;
        } else if (event.key === 'Enter' || event.key === ' ') {
            this.selectOption(this.activeOption);
        } else if (event.key === 'Escape') {
            this.sortExpanded = false;
        }
    }
}

export default liveSearchSort;
